import { client } from '@/api/client';

// A VIN is globally unique to one physical vehicle. This guards against the
// same VIN being entered on two different driver profiles or assets (which
// silently breaks owner↔truck linkage and asset auto-creation).
//
// Returns a human-readable conflict message if the VIN is already used by a
// DIFFERENT driver profile or asset, otherwise null. On a lookup failure it
// returns null (does not block the user) — it's a strong guard for the normal
// case, not a hard gate that traps people during a backend hiccup.
export async function findVinConflict(vin, { excludeProfileId = null, excludeAssetId = null } = {}) {
  const v = (vin || '').trim();
  if (!v) return null;
  try {
    const [byTruck, byTrailer, byAsset] = await Promise.all([
      client.entities.DriverProfile.filter({ truck_vin: v }).catch(() => []),
      client.entities.DriverProfile.filter({ trailer_vin: v }).catch(() => []),
      client.entities.Asset.filter({ vin: v }).catch(() => []),
    ]);
    const profHit = [...byTruck, ...byTrailer].find(p => p && p.id !== excludeProfileId);
    if (profHit) {
      return `VIN ${v} is already on ${profHit.full_name || 'another driver'}'s profile. A VIN can only belong to one vehicle.`;
    }
    const assetHit = byAsset.find(a => a && a.id !== excludeAssetId);
    if (assetHit) {
      return `VIN ${v} is already on asset Unit ${assetHit.unit_number || assetHit.id}. A VIN can only belong to one vehicle.`;
    }
    return null;
  } catch {
    return null;
  }
}
